import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputFile } from '@/components/forms/form-input-file';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  createCoachPaymentsByCsv,
  getCoachPayment,
  getOngoingPayments,
  getTotalSumFromLifetime,
} from '@/lib/api-calls';
import { csvFormSchema, downloadCSV, parseCsvAndUpload } from '@/lib/csv-utils';
import { pluralize } from '@/lib/string-utils';
import { PostCoachPaymentCSVFormKeys, postCoachPaymentCSVRow } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { FromOptions } from './from-options';
import { Button } from '@/components/ui/button';

interface CsvPaymentEntryProps {
  from: FromOptions;
}

const formSchema = csvFormSchema;

type SubmitResults = {
  successRowsCount: number;
  errors: string[];
  fileName?: string;
};

export const CsvPaymentEntry = ({ from }: CsvPaymentEntryProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [results, setResults] = useState<SubmitResults | undefined>(undefined);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createCoachPaymentsByCsv,
  });

  const groupedErrors = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const msg of results?.errors ?? []) {
      const match = msg.match(/^(Row \d+): (.+)$/);
      if (match) {
        const [, rowKey, text] = match;
        if (!groups.has(rowKey)) groups.set(rowKey, []);
        groups.get(rowKey)!.push(text);
      } else {
        if (!groups.has(msg)) groups.set(msg, []);
        groups.get(msg)!.push(msg);
      }
    }
    return Array.from(groups.entries()).map(([key, msgs]) => `${key}: ${msgs.join('; ')}`);
  }, [results?.errors]);

  const onSubmit = async ({ formData }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: postCoachPaymentCSVRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(PostCoachPaymentCSVFormKeys.paymentsCSV, file);
        const summary = await mutateAsync(uploadForm);
        return {
          successRowsCount: summary.succeeded,
          errors: summary.failures,
          fileName: file.name,
        };
      },
      onToast: toast,
      onSetErrors: (errors) => {
        setResults((prev) => ({ ...(prev ?? {}), errors, successRowsCount: 0 }));
      },
    });

    const { successRowsCount } = result;
    const fileName = formData.csvFile.item(0)?.name;

    if (successRowsCount > 0) {
      toast({ title: `${successRowsCount} ${pluralize(successRowsCount, 'payment', 'payments')} created` });

      if (from === FromOptions.teacherPay) {
        queryClient.invalidateQueries(getOngoingPayments());
        queryClient.invalidateQueries(getTotalSumFromLifetime());
      }
      if (from === FromOptions.payments) {
        queryClient.invalidateQueries(getCoachPayment());
        queryClient.invalidateQueries(getTotalSumFromLifetime());
      }

      setResults((prev) => ({ errors: prev?.errors ?? [], successRowsCount, fileName: fileName }));
    }
  };

  const onDownload = () => {
    if (!results?.errors) return;
    const name = results?.fileName;

    const header = 'Row,Error';
    const rows = groupedErrors.map((entry) => {
      const colonIndex = entry.indexOf(': ');
      const row = colonIndex !== -1 ? entry.slice(0, colonIndex) : '';
      const error = colonIndex !== -1 ? entry.slice(colonIndex + 2) : entry;
      return `"${row}","${error.replace(/"/g, '""')}"`;
    });
    downloadCSV([header, ...rows].join('\n'), name ? `${name}_errors.csv` : 'payment-errors.csv');
  };

  const onClose = () => {
    onDownload();
    setResults(undefined);
  };

  return (
    <FormModal
      modalTitle="Add payments by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
      cancelBtnLabel={'Cancel'}
      confirmBtnLabel={'Add'}
      hideCancelButton={results !== undefined && !isPending}
      hideConfirmButton={results !== undefined && !isPending}
      onClose={onClose}
      preventExternalClose
      footerContent={({ form }) => {
        if (results === undefined || isPending) return null;
        return (
          <>
            <Button
              type="button"
              variant={'secondary'}
              onClick={() => {
                const fileName = form.getValues('csvFile').item(0)?.name;

                const header = 'Row,Error';
                const rows = groupedErrors.map((entry) => {
                  const colonIndex = entry.indexOf(': ');
                  const row = colonIndex !== -1 ? entry.slice(0, colonIndex) : '';
                  const error = colonIndex !== -1 ? entry.slice(colonIndex + 2) : entry;
                  return `"${row}","${error.replace(/"/g, '""')}"`;
                });
                downloadCSV([header, ...rows].join('\n'), fileName ? `${fileName}_errors.csv` : 'payment-errors.csv');
                return;
              }}
            >
              Download
            </Button>
          </>
        );
      }}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormInputFile
                form={form}
                name="csvFile"
                label="Payments CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href="https://docs.google.com/spreadsheets/d/1WibHQv6PpwYoUPrPr_nfaUQU6UEOY3aRIGZV6BJPc_A/edit?gid=258658669#gid=258658669"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template (to update)
                </a>{' '}
                for CSV format. Export the sheet as csv file and edit it. Do NOT change header row.
              </span>
            </div>
            {results && results.successRowsCount > 0 && (
              <div className="w-full mb-4 bg-[#F2F2F7] py-2">
                <p className="border-l-4 border-green-400 pl-2">
                  Import {results?.successRowsCount} {pluralize(results?.successRowsCount ?? 0, 'payment', 'payments')}{' '}
                  successfully.
                </p>
              </div>
            )}
            {groupedErrors.length > 0 && (
              <div className="w-full mb-4 bg-[#F2F2F7] py-2">
                <p className="border-l-4 border-red-400 pl-2">
                  Failed to import {groupedErrors.length} {pluralize(groupedErrors.length, 'payment', 'payments')}.
                </p>
                <ul className="list-disc pl-8">
                  {groupedErrors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
